const getAuthToken = () => {
  try {
    const tokenData = sessionStorage.getItem("authToken") || localStorage.getItem("authToken");
    if (!tokenData) return null;
    try {
      const parsed = JSON.parse(tokenData);
      return parsed.access || parsed.access_token || parsed.token;
    } catch {
      // Token is a raw JWT string, not JSON
      return tokenData;
    }
  } catch (e) {
    console.error("Error retrieving auth token:", e);
  }
  return null;
};

// Builds an Error carrying the backend's message (DRF `detail`, `error`, or the
// first field error) so callers can show what actually went wrong.
const backendError = async (response: Response, fallback: string): Promise<Error> => {
    let message = "";
    try {
        const text = await response.text();
        console.log(text);
        try {
            const body = JSON.parse(text);
            if (typeof body === "string") message = body;
            else if (body?.detail) message = String(body.detail);
            else if (body?.error) message = String(body.error);
            else if (body?.message) message = String(body.message);
            else if (body && typeof body === "object") {
                const [field, value] = Object.entries(body)[0] || [];
                if (field) message = `${field}: ${Array.isArray(value) ? value.join(" ") : String(value)}`;
            }
        } catch {
            message = text && text.length < 300 ? text : "";
        }
    } catch {
        // ignore body read errors
    }
    return new Error(message ? `${fallback}: ${message}` : `${fallback} (HTTP ${response.status})`);
};

export const getAuditLawImplementations = async () => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followups/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        console.log(response);
        throw new Error("Failed to get Law Implementation follow up request");
    }
    return response.json();
};

// Sector (audit) follow-ups assigned to the calling prosecutor's team.
// Any prosecutor role (legal research / awareness / translation); user id and roles come from the token.
export const getMyTeamAudits = async () => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followups/my-team-audits/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        throw await backendError(response, "Failed to get my team audit follow ups");
    }
    return response.json();
};

export const getAuditLawImplementation = async (id: string) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followups/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to get Law Implementation follow up request");
    }
    return response.json();
};

export const createAuditLawImplementation = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followups/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const updateAuditLawImplementation = async (id: string, data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followups/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "PATCH",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to update Law Implementation follow up request");
    }
    return response.json();
};

export const getLawImplementations = async () => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followups/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        console.log(response);
        throw new Error("Failed to get Law Implementation follow up request");
    }
    return response.json();
};

export const getLawImplementation = async (id: string) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followups/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to get Law Implementation follow up request");
    }
    return response.json();
};

export const getLawImplementationsByRegistryNumber = async (registryNumber: string) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followups/by-registry-number/?registry_number=${registryNumber}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        console.log(response);
        throw new Error("Failed to get Law Implementation follow up request by registry number");
    }
    return response.json();
};

export const getLawImplementationsByRegistryAndUser = async (registryNumber: string, userId: string) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followups/by-registry-and-user/?registry_number=${registryNumber}&user_id=${userId}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "GET",
        }
    );
    if (!response.ok) {
        console.log(response);
        throw new Error("Failed to get Law Implementation follow up request by registry and user");
    }
    return response.json();
};

export const createLawImplementation = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followups/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const updateLawImplementation = async (id: string, data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followups/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "PATCH",
            body: data
        }
    );
    if (!response.ok) {
        throw await backendError(response, "Failed to update Law Implementation follow up request");
    }
    return response.json();
};


export const createLawImplementationRespondbackFeedback = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/respond-back-feedbacks/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const createLawImplementationFeedback = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/feedbacks/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};


export const createLawImplementationRemark = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/remarks/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const createLawImplementationTeam = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followup-teams/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const createLawImplementationProsecutorAssignment = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/teams/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        throw await backendError(response, "Failed to assign prosecutor");
    }
    return response.json();
};

export const updateLawImplementationProsecutorAssignment = async (id: string, data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/teams/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "PATCH",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const deleteLawImplementationProsecutorAssignment = async (id: string) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/teams/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "DELETE",
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to remove Law Implementation team member");
    }
    return true;
};

export const createLawImplementationEvidence = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/evidences/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation follow up request");
    }
    return response.json();
};

export const createAuditFollowupReport = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followup-reports/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Audit Follow-up Report");
    }
    return response.json();
};

export const createLawImplementationEvidenceCollectors = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/evidence_collectors/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to create Law Implementation evidence collector");
    }
    return response.json();
};

export const createLawImplementationTargets = async (data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followup-law-sectors/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "POST",
            body: data
        }
    );
    if (!response.ok) {
        throw await backendError(response, "Failed to save identified law or sector");
    }
    return response.json();
};

export const updateLawImplementationTargets = async (id: string, data: FormData) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followup-law-sectors/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "PATCH",
            body: data
        }
    );
    if (!response.ok) {
        throw await backendError(response, "Failed to update identified law or sector");
    }
    return response.json();
};

export const deleteLawImplementationTargets = async (id: string) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followup-law-sectors/${id}/`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            method: "DELETE",
        }
    );
    if (!response.ok) {
        const errorBody = await response.text();
        console.log(errorBody);
        throw new Error("Failed to delete Law Implementation follow up request");
    }
};



// services/teamService.ts
export const updateTeamLeader = async ({ id, payload }: { id: string; payload: any }) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/teams/${id}/`,
        {
            method: "PATCH",
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload)
        }
    );

    if (!response.ok) {
        const errorBody = await response.text();
        console.error('Error updating team leader:', errorBody);
        throw new Error("Failed to update Law Implementation follow up request");
    }

    return response.json();
};

export const updateTeamName = async ({ id, payload }: { id: string; payload: any }) => {
    const token = getAuthToken();
    const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/followup-teams/${id}/`,
        {
            method: "PATCH",
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload)
        }
    );

    if (!response.ok) {
        const errorBody = await response.text();
        console.error('Error updating team name:', errorBody);
        throw new Error("Failed to update Law Implementation follow up request");
    }

    return response.json();
};

